import axios from 'axios'
import { reactive } from 'vue'

const TOKEN_KEY = 'auth_token_default'
const REFRESH_INTERVAL_MINUTES = 120

const state = reactive<{
  data: Record<string, any> | null
  authenticated: boolean | null
  loaded: boolean
}>({
  data: null,
  authenticated: null,
  loaded: false,
})

function getToken(): string | null {
  const raw = localStorage.getItem(TOKEN_KEY)
  return raw ? JSON.parse(raw) : null
}

function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(token))
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    localStorage.removeItem(TOKEN_KEY)
    delete axios.defaults.headers.common['Authorization']
  }
}

function persistTokenFromResponse(response: Record<string, any>) {
  const header = response.headers?.authorization || response.headers?.Authorization
  if (!header) return
  setToken(header.replace(/^Bearer\s+/i, ''))
}

const auth = {
  state,
  check(permission?: string): boolean {
    if (!state.authenticated) return false
    if (!permission) return true
    return (state.data?.permissions_list ?? []).includes(permission)
  },
  user() {
    return state.data
  },
  async fetch() {
    const response = await axios.get('/me')
    persistTokenFromResponse(response)
    state.data = response.data.data
    state.authenticated = true
    state.loaded = true
    return response
  },
  async login({ data }: { data: { username: string; password: string } }) {
    const response = await axios.post('auth/login', data)
    persistTokenFromResponse(response)
    await auth.fetch()
    return response
  },
  async logout({ makeRequest = true }: { makeRequest?: boolean } = {}) {
    if (makeRequest) {
      await axios.post('auth/logout').catch(() => {})
    }
    setToken(null)
    state.data = null
    state.authenticated = false
  },
  async refresh() {
    const response = await axios.post('/auth/refresh')
    persistTokenFromResponse(response)
    return response
  },
}

const existingToken = getToken()
if (existingToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`
  setInterval(() => {
    auth.refresh().catch(() => {})
  }, REFRESH_INTERVAL_MINUTES * 60 * 1000)
} else {
  state.authenticated = false
  state.loaded = true
}

export function useAuth() {
  return auth
}

export default auth
